import { useState, useEffect, useRef } from "react";
import "../styles/FAQpage.css";
import MainFooter from "../components/MainFooter";

const categories = [
  { id: "general",   icon: "🏠", label: "General" },
  { id: "organiser", icon: "🎪", label: "Event Organisers" },
  { id: "attendee",  icon: "🎟", label: "Attendees" },
  { id: "speaker",   icon: "🎤", label: "Speakers" },
  { id: "sponsor",   icon: "🤝", label: "Sponsors & Exhibitors" },
  { id: "technical", icon: "⚙️", label: "Technical Support" },
  { id: "privacy",   icon: "🔒", label: "Privacy & Data" },
];

const faqs = {
  general: [
    {
      q: "What is Eventfork.com?",
      a: "Eventfork.com is a comprehensive event management platform that enables individuals and organisations to create, manage, promote, and attend events of all types — including conferences, meetups, expos, workshops, webinars, hackathons, and hybrid gatherings. We provide tools for registration, ticketing, speaker management, sponsor coordination, and real-time analytics.",
    },
    {
      q: "Who can use Eventfork.com?",
      a: "Eventfork is open to anyone aged 18 and above. Our platform caters to event organisers of all sizes — from solo community meetup hosts to large enterprises running multi-track international conferences. Attendees, speakers, and sponsors can also create profiles to participate in events.",
    },
    {
      q: "Is Eventfork.com free to use?",
      a: "Creating an account and listing free events on Eventfork is completely free. For paid events, Eventfork charges a small platform service fee on each ticket transaction. Premium plans are also available for organisers requiring advanced features such as custom branding, dedicated support, and API access. Full pricing details are available in our Pricing section.",
    },
    {
      q: "What types of events can I create on Eventfork?",
      a: "Eventfork supports a wide range of event formats including conferences (single and multi-track, multi-day), meetups, trade shows and expos, workshops and training sessions, webinars and virtual events, hybrid events, hackathons, product launches, and private corporate events.",
    },
    {
      q: "Is there a mobile app for Eventfork?",
      a: "Yes. Eventfork offers a mobile application for both iOS and Android. Attendees can use the app to browse events, manage registrations, access digital tickets, and check in on-site. Organisers can monitor registrations and send announcements in real time.",
    },
    {
      q: "What languages and currencies does Eventfork support?",
      a: "Eventfork currently supports English, Hindi, and several regional languages. For payments, we support INR, USD, EUR, GBP, and AED, with additional currencies being added regularly. Currency is selected by the Event Organiser at the time of event creation.",
    },
  ],
  organiser: [
    {
      q: "How do I create an event on Eventfork?",
      a: "To create an event, log in to your Eventfork account and click 'Create Event' from your Organiser Dashboard. You will be guided through a step-by-step process to enter your event name, description, date and time, venue or virtual link, event type, agenda, ticket types, and speaker/sponsor details. Once submitted, your event will be reviewed and published within a few minutes.",
    },
    {
      q: "Can I manage multiple events simultaneously?",
      a: "Yes. The Organiser Dashboard allows you to manage multiple events from a single interface. You can switch between events, track registrations, send communications, and view analytics for each event independently. There is no limit to the number of active events you can manage.",
    },
    {
      q: "How do I set up different ticket types for my event?",
      a: "During event creation or from your event settings, navigate to the 'Tickets' section. You can create multiple ticket tiers (e.g., Early Bird, General Admission, VIP, Group Pass) with individual pricing, availability limits, sale windows, and access permissions. You can also create promo codes and discount offers for specific ticket types.",
    },
    {
      q: "Can I customise the event registration form?",
      a: "Yes. Eventfork provides a fully customisable registration form builder. You can add standard fields (name, email, phone) as well as custom fields such as company name, dietary requirements, t-shirt size, session preferences, and more. Fields can be marked as required or optional.",
    },
    {
      q: "How do I manage event check-in and attendee badges?",
      a: "Eventfork provides a QR code-based check-in system accessible via the Eventfork mobile app or a web-based check-in dashboard. Each registered attendee receives a unique QR code that can be scanned on arrival. Digital and printable name badges are auto-generated from registration data.",
    },
    {
      q: "Can I run a hybrid event (in-person + virtual) on Eventfork?",
      a: "Yes. Eventfork fully supports hybrid events. You can create separate ticket types for in-person and virtual attendance, manage both streams from a single dashboard, integrate with popular video conferencing platforms, and provide virtual attendees with unique access links.",
    },
    {
      q: "How do I send communications to my attendees?",
      a: "From your Organiser Dashboard, navigate to the 'Communications' section. You can send email announcements, event reminders, schedule updates, and post-event follow-ups to all attendees, specific ticket holders, or custom segments. Pre-scheduled messages and automated reminders are also available.",
    },
    {
      q: "Can I cancel or reschedule my event?",
      a: "Yes. You can cancel or reschedule your event from the event settings. If you cancel a paid event, our platform will notify all registered attendees and you are required to process refunds within 7 business days. For rescheduled events, attendees are notified automatically and given the option to request a refund.",
    },
    {
      q: "What analytics are available to Event Organisers?",
      a: "Eventfork provides a comprehensive analytics dashboard including real-time registration counts, ticket sales breakdown, revenue tracking, attendee demographics, source tracking (how attendees found your event), check-in rates, session attendance, and post-event survey results.",
    },
  ],
  attendee: [
    {
      q: "How do I register for an event?",
      a: "Visit the event page on Eventfork.com and click 'Register' or 'Get Tickets'. Select your preferred ticket type, complete the registration form, and proceed to payment (if applicable). You will receive a confirmation email with your ticket and QR code within a few minutes of completing registration.",
    },
    {
      q: "Where can I find my tickets?",
      a: "Your tickets are accessible in your Eventfork account under 'My Tickets'. You will also receive your ticket with QR code via email upon registration. The Eventfork mobile app provides easy access to all your upcoming and past event tickets.",
    },
    {
      q: "Can I transfer my ticket to someone else?",
      a: "Ticket transfers are allowed unless the Event Organiser has restricted this option. To transfer a ticket, go to 'My Tickets' in your account, select the ticket you wish to transfer, and enter the recipient's email address. The recipient will receive an email to accept the transfer.",
    },
    {
      q: "What is the refund policy for event tickets?",
      a: "Refund eligibility is determined by the Event Organiser's refund policy, which is displayed on the event registration page before you complete your purchase. If an event is cancelled by the Organiser, you are entitled to a full refund of the ticket price. Platform service fees may be non-refundable. To request a refund, contact the Event Organiser directly or raise a request through your Eventfork account.",
    },
    {
      q: "How do I access a virtual or hybrid event?",
      a: "For virtual or hybrid events, your unique access link will be included in your confirmation email and accessible in your Eventfork account under 'My Tickets'. Access links are personal and must not be shared. Ensure you have a stable internet connection and the required software or browser before the event begins.",
    },
    {
      q: "Can I attend multiple sessions within a conference?",
      a: "Yes. For multi-track conferences, you can view the full agenda and add individual sessions to your personal schedule within the Eventfork app or web portal. Some sessions may require pre-registration if capacity is limited.",
    },
    {
      q: "What do I do if I can't check in at the event?",
      a: "If you encounter any issues with check-in, approach the event registration desk and show your QR code or booking confirmation email. If the issue persists, contact the Event Organiser or use the Eventfork Help option in the app to raise a support request.",
    },
  ],
  speaker: [
    {
      q: "How do I become a speaker at an event on Eventfork?",
      a: "There are two ways to become a speaker: (1) Apply through the event's Call for Papers (CFP) if the Organiser has published one — navigate to the event page and click 'Submit a Proposal'; or (2) Accept a direct speaker invitation sent by the Event Organiser. You will receive an email with a link to create or update your speaker profile.",
    },
    {
      q: "How do I create and manage my speaker profile?",
      a: "Once invited or registered as a speaker, log in to Eventfork and navigate to 'Speaker Profile' in your account settings. You can upload a professional headshot, write a biography, add social media links, list your areas of expertise, and specify any technical or accessibility requirements for your session.",
    },
    {
      q: "Can I submit multiple session proposals?",
      a: "Yes, you can submit multiple proposals to a single event's CFP. Each proposal should include a session title, abstract, format (talk, panel, workshop), target audience, and estimated duration. The Organiser will review and notify you of acceptance decisions.",
    },
    {
      q: "Will my session be recorded or live-streamed?",
      a: "Recording and live-streaming policies vary by event. The Event Organiser is required to disclose recording intentions on the event page and in the speaker invitation. You will be asked to provide explicit consent before any session is recorded. If you have concerns, please communicate them to the Organiser directly.",
    },
    {
      q: "What technical support is available for speakers?",
      a: "Eventfork provides speakers with access to a session management portal where you can upload presentation slides, add co-speakers, specify AV requirements, and communicate with the Organiser. For virtual and hybrid sessions, Eventfork offers a pre-event technical rehearsal option to test your setup.",
    },
    {
      q: "Is speaker compensation managed through Eventfork?",
      a: "Speaker fees, travel reimbursements, and other compensation arrangements are solely between the Speaker and the Event Organiser. Eventfork does not facilitate or process speaker payments. We recommend formalising compensation agreements directly with the Organiser before the event.",
    },
  ],
  sponsor: [
    {
      q: "How can my organisation sponsor an event on Eventfork?",
      a: "To sponsor an event, contact the Event Organiser directly via the contact details on the event page. Alternatively, some events publish a 'Become a Sponsor' option on their event page with available sponsorship tiers and packages. Eventfork does not manage sponsorship agreements but provides the Organiser with tools to display and manage sponsor listings.",
    },
    {
      q: "How are sponsor logos and listings displayed on event pages?",
      a: "Event Organisers can create sponsorship tiers (e.g., Platinum, Gold, Silver) and upload sponsor logos, descriptions, and website links for each. Sponsor listings appear on the public event page, event app, and digital programme. Display prominence is typically linked to the sponsorship tier selected.",
    },
    {
      q: "How does exhibitor booth management work for expos?",
      a: "For expo-style events, Eventfork provides Organisers with an interactive floor plan and booth assignment tool. Exhibitors are provided with a dedicated exhibitor portal where they can update their booth profile, upload marketing materials, manage their team's access, and view visitor statistics.",
    },
    {
      q: "Can exhibitors collect leads from attendees at events?",
      a: "Yes. Eventfork's lead retrieval feature allows exhibitors to scan attendee badges (QR codes) to capture contact information. Lead data is only collected with the Attendee's consent, as configured by the Event Organiser's data settings. Collected leads are accessible in the Exhibitor Portal and can be exported in CSV format.",
    },
    {
      q: "Are there virtual sponsorship options for online events?",
      a: "Yes. For virtual and hybrid events, Eventfork supports digital sponsorship placements including virtual booth pages, sponsored session banners, branded email communications, logo placement in the virtual event lobby, and sponsored push notifications to attendees.",
    },
  ],
 
  technical: [
    {
      q: "Which browsers and devices does Eventfork support?",
      a: "Eventfork.com is fully supported on the latest versions of Google Chrome, Mozilla Firefox, Apple Safari, and Microsoft Edge. For mobile devices, we recommend using the Eventfork mobile app available on iOS 14+ and Android 9+. Internet Explorer is not supported.",
    },
    {
      q: "How do I integrate Eventfork with third-party tools?",
      a: "Eventfork offers native integrations with popular CRM platforms (Salesforce, HubSpot), marketing tools (Mailchimp, Marketo), video conferencing solutions (Zoom, Google Meet, Microsoft Teams), and analytics platforms. A full list of integrations is available in your Organiser Dashboard under 'Integrations'. For custom integrations, access our REST API via the Developer Portal.",
    },
    {
      q: "Is an API available for custom integrations?",
      a: "Yes. Eventfork provides a comprehensive REST API for developers who need to build custom integrations or automate workflows. API documentation, authentication guides, and sandbox credentials are available at developers.eventfork.com. API access is available on Professional and Enterprise plans.",
    },
    {
      q: "What should I do if the platform is not loading or responding?",
      a: "If you experience issues with the platform, first check the Eventfork Status page at status.eventfork.com for any ongoing incidents. Clear your browser cache and cookies, and try a different browser or device. If the issue persists, contact our technical support team at info@eventfork.com with a description of the issue and any error messages.",
    },
    {
      q: "How do I set up virtual event streaming on Eventfork?",
      a: "Eventfork supports embedded streaming via Zoom, YouTube Live, Vimeo, and custom RTMP streams. To configure streaming for your event, go to your event settings and navigate to 'Virtual Event Setup'. You can paste your streaming link or connect your Zoom account. Attendees with valid tickets will automatically receive secure access links.",
    },
  ],
  privacy: [
    {
      q: "How does Eventfork protect my personal data?",
      a: "Eventfork uses industry-standard security measures including TLS/SSL encryption for all data in transit, AES-256 encryption for sensitive data at rest, strict access controls, regular security audits, and formal incident response procedures. We comply with applicable data protection regulations including the Information Technology Act, 2000 (India) and GDPR (where applicable).",
    },
    {
      q: "Does Eventfork sell my personal data?",
      a: "No. Eventfork does not sell, rent, or trade your personal data to any third party under any circumstances. Your data is used solely to operate the platform, provide our services, and as described in our Privacy Policy. Third-party service providers we work with are contractually bound to process data only on our instructions.",
    },
    {
      q: "What data does the Event Organiser receive about me?",
      a: "When you register for an event, the Event Organiser receives the information you provide during registration — typically your name, email address, and any custom fields they have set up (e.g., company name, dietary requirements). Organisers must use this data solely to manage your participation in their event.",
    },
    {
      q: "How can I delete my Eventfork account and data?",
      a: "You can request account deletion by going to 'Account Settings' and selecting 'Delete Account', or by emailing info@eventfork.com. Upon deletion, your personal data will be removed from our active systems within 30 days, subject to any legal retention obligations. Note that deleting your account will cancel all active registrations.",
    },
    {
      q: "Can I download a copy of my personal data?",
      a: "Yes. You have the right to request a copy of all personal data we hold about you. To submit a data access request, email info@eventfork.com with the subject 'Data Access Request'. We will respond within 30 days with a structured export of your data in a machine-readable format.",
    },
    {
      q: "How does Eventfork use cookies?",
      a: "Eventfork uses essential cookies (required for platform functionality), analytics cookies (to understand usage patterns and improve our services), and marketing cookies (to deliver relevant event recommendations, with your consent). You can manage your cookie preferences at any time through the Cookie Settings option in the website footer.",
    },
  ],
};

/* ─── Sub-component: single FAQ accordion item ────────────────── */
function FAQItem({ id, item, open, toggle, searchTerm }) {
  const highlight = (text) => {
    if (!searchTerm) return text;
    const regex = new RegExp(
      `(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "gi"
    );
    return text.split(regex).map((part, i) =>
      regex.test(part) ? <mark key={i}>{part}</mark> : part
    );
  };

  return (
    <div className={`faq-card${open ? " open" : ""}`}>
      <button
        className={`faq-question${open ? " open" : ""}`}
        onClick={toggle}
        aria-expanded={open}
      >
        <span className="faq-question__text">{highlight(item.q)}</span>
        <span className={`faq-question__icon${open ? " open" : ""}`}>＋</span>
      </button>

      {open && (
        <div className="faq-answer">
          <div className="faq-answer__inner">{highlight(item.a)}</div>
        </div>
      )}
    </div>
  );
}

/* ─── Main component ──────────────────────────────────────────── */
export default function FAQPage() {
  const [activeCategory, setActiveCategory] = useState("general");
  const [openItems, setOpenItems]           = useState({});
  const [search, setSearch]                 = useState("");
  const [scrollPct, setScrollPct]           = useState(0);
  const [searchResults, setSearchResults]   = useState(null);
  const mainRef = useRef(null);

  /* Track scroll progress */
  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      setScrollPct(
        (window.scrollY / (doc.scrollHeight - doc.clientHeight)) * 100
      );
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toggleItem = (key) =>
    setOpenItems((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleSearch = (val) => {
    setSearch(val);
    if (!val.trim()) { setSearchResults(null); return; }
    const q = val.toLowerCase();
    const results = [];
    Object.entries(faqs).forEach(([catId, items]) => {
      const cat = categories.find((c) => c.id === catId);
      items.forEach((item, idx) => {
        if (
          item.q.toLowerCase().includes(q) ||
          item.a.toLowerCase().includes(q)
        ) {
          results.push({ catId, cat, item, idx });
        }
      });
    });
    setSearchResults(results);
  };

  const handleExpandAll = () => {
    const allOpen = {};
    (faqs[activeCategory] || []).forEach((_, i) => {
      allOpen[`${activeCategory}-${i}`] = true;
    });
    setOpenItems(allOpen);
  };

  const activeCat = categories.find((c) => c.id === activeCategory);

  /* Group search results by category */
  const groupedResults = searchResults
    ? searchResults.reduce((acc, r) => {
        if (!acc[r.catId]) acc[r.catId] = [];
        acc[r.catId].push(r);
        return acc;
      }, {})
    : null;

  return (
    <div>
      {/* ── Scroll progress bar ── */}
      <div
        className="faq-progress"
        style={{ width: `${scrollPct}%` }}
        aria-hidden="true"
      />

      {/* ── Hero header ── */}
      <header className="faq-header">
        <div className="faq-header__orb-1" aria-hidden="true" />
        <div className="faq-header__orb-2" aria-hidden="true" />
        <div className="faq-header__inner">
          <div className="faq-header__tags">
            <span className="tag-chip tag-chip--ghost"> Help Centre</span>
            <span className="tag-chip tag-chip--ghost">FAQ</span>
          </div>

          <h1 className="faq-header__title">Frequently Asked Questions</h1>

          <p className="faq-header__subtitle">
            Find answers to common questions about creating events, managing
            attendees, speakers, sponsors, payments, and more on Eventfork.com.
          </p>

          {/* Search */}
          <div className="faq-search-wrapper">
            <span className="faq-search-icon" aria-hidden="true">🔍</span>
            <input
              className="faq-search-box"
              type="search"
              placeholder="Search all FAQs — e.g. 'refund', 'ticket transfer', 'speaker profile'…"
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              aria-label="Search FAQs"
            />
          </div>
        </div>
      </header>

      {/* ── Breadcrumb ── */}
      <nav className="faq-breadcrumb" aria-label="Breadcrumb">
        <div className="faq-breadcrumb__inner">
          <span className="faq-breadcrumb__link">Home</span>
          <span aria-hidden="true">›</span>
          <span className="faq-breadcrumb__link">Support</span>
          <span aria-hidden="true">›</span>
          <span>FAQ</span>
        </div>
      </nav>

      {/* ── Stats row ── */}
      <section className="faq-stats" aria-label="Platform statistics">
        <div className="faq-stats__grid">
          {[
            ["50+", "Questions Answered"],
            [String(categories.length), "Topic Categories"],
            ["24/7", "Platform Available"],
            ["< 24h", "Support Response"],
          ].map(([num, label]) => (
            <div key={label} className="stat-card">
              <div className="stat-card__num">{num}</div>
              <div className="stat-card__label">{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Body ── */}
      <div className="faq-body">

        {/* ── Sidebar ── */}
        <aside className="faq-sidebar" aria-label="FAQ navigation">

          {/* Category nav */}
          <div className="faq-sidebar__card">
            <div className="faq-sidebar__card-header">
              <p>Browse by Topic</p>
            </div>
            <nav className="faq-sidebar__nav">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  className={`cat-btn${
                    activeCategory === cat.id && !searchResults ? " active" : ""
                  }`}
                  onClick={() => {
                    setActiveCategory(cat.id);
                    setSearch("");
                    setSearchResults(null);
                  }}
                  aria-current={
                    activeCategory === cat.id && !searchResults
                      ? "page"
                      : undefined
                  }
                >
                  <span className="cat-btn__icon" aria-hidden="true">
                    {cat.icon}
                  </span>
                  <span>{cat.label}</span>
                  <span className="cat-btn__count" aria-label={`${(faqs[cat.id] || []).length} questions`}>
                    {(faqs[cat.id] || []).length}
                  </span>
                </button>
              ))}
            </nav>
          </div>

          {/* Still need help? */}
          <div className="faq-sidebar__card">
            <div className="faq-sidebar__card-header faq-sidebar__card-header--light">
              <p>Still Need Help?</p>
            </div>
            <div className="faq-sidebar__body">
              {[
                { icon: "📧", label: "Email Support",  val: "info@eventfork.com", href: "mailto:info@eventfork.com" },
                { icon: "⚖",  label: "Legal Queries",  val: "info@eventfork.com",   href: "mailto:info@eventfork.com"   },
              
              ].map((item) => (
                <div key={item.label} className="faq-contact-item">
                  <p className="faq-contact-item__label">
                    {item.icon} {item.label}
                  </p>
                  <a className="faq-contact-item__link" href={item.href}>
                    {item.val}
                  </a>
                </div>
              ))}
              
            </div>
          </div>

         
        </aside>

        {/* ── Main ── */}
        <main ref={mainRef} className="faq-main">

          {/* Section header bar */}
          <div className="faq-section-header">
            <div>
              {searchResults ? (
                <>
                  <h2 className="faq-section-header__title">
                    Search Results for "{search}"
                  </h2>
                  <p className="faq-section-header__meta">
                    {searchResults.length} result
                    {searchResults.length !== 1 ? "s" : ""} found across{" "}
                    {[...new Set(searchResults.map((r) => r.catId))].length}{" "}
                    {[...new Set(searchResults.map((r) => r.catId))].length === 1
                      ? "category"
                      : "categories"}
                  </p>
                </>
              ) : (
                <>
                  <h2 className="faq-section-header__title">
                    <span aria-hidden="true">{activeCat?.icon}</span>
                    {activeCat?.label}
                  </h2>
                  <p className="faq-section-header__meta">
                    {(faqs[activeCategory] || []).length} questions in this
                    category
                  </p>
                </>
              )}
            </div>

            {!searchResults && (
              <button
                className="faq-expand-btn"
                onClick={handleExpandAll}
                aria-label="Expand all questions"
              >
                Expand All ↕
              </button>
            )}
          </div>

          {/* Empty search state */}
          {searchResults && searchResults.length === 0 && (
            <div className="faq-empty" role="status">
              <p className="faq-empty__icon" aria-hidden="true">🔍</p>
              <p className="faq-empty__title">No results found</p>
              <p className="faq-empty__body">
                Try different keywords or browse by topic using the sidebar.
              </p>
            </div>
          )}

          {/* FAQ list — search mode */}
          {searchResults && searchResults.length > 0 &&
            Object.entries(groupedResults).map(([catId, items]) => {
              const cat = categories.find((c) => c.id === catId);
              return (
                <div key={catId} className="faq-group">
                  <div className="faq-group-label" aria-hidden="true">
                    <span>{cat?.icon}</span>
                    <span className="faq-group-label__text">{cat?.label}</span>
                    <div className="faq-group-label__divider" />
                  </div>
                  {items.map(({ item, idx }) => (
                    <FAQItem
                      key={`search-${catId}-${idx}`}
                      id={`search-${catId}-${idx}`}
                      item={item}
                      open={!!openItems[`search-${catId}-${idx}`]}
                      toggle={() => toggleItem(`search-${catId}-${idx}`)}
                      searchTerm={search}
                    />
                  ))}
                </div>
              );
            })
          }

          {/* FAQ list — category mode */}
          {!searchResults &&
            (faqs[activeCategory] || []).map((item, idx) => (
              <FAQItem
                key={`${activeCategory}-${idx}`}
                id={`${activeCategory}-${idx}`}
                item={item}
                open={!!openItems[`${activeCategory}-${idx}`]}
                toggle={() => toggleItem(`${activeCategory}-${idx}`)}
                searchTerm=""
              />
            ))
          }

          {/* Bottom CTA */}
          {!searchResults && (
            <div className="faq-cta" role="complementary" aria-label="Contact support">
              <div>
                <p className="faq-cta__title">
                  Didn't find what you were looking for?
                </p>
                
              </div>
              <a
                href="mailto:info@eventfork.com"
                className="faq-cta__btn"
              >
                Contact Support →
              </a>
            </div>
          )}
        </main>
      </div>

      <MainFooter/>
    </div>
  );
}
